import { useTranslation } from '@/hooks/useTranslation';
import { Card } from '@/components/ui/card/Card';
import { cn } from '@/lib/utils';
import type { ReferenceStats } from '../../utils/dashboard';

export type KpiStripProps = {
  stats: ReferenceStats;
  className?: string;
};

type CellProps = {
  label: string;
  value: string;
  unit: string;
  sub?: string;
  accent?: boolean;
};

const fmt = new Intl.NumberFormat();
const fmtDec = (n: number, decimals = 1) => n.toFixed(decimals);

function Cell({ label, value, unit, sub, accent }: CellProps) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 px-4 py-3 border-r border-border last:border-r-0',
        accent && 'bg-primary/5'
      )}
    >
      <span className="eyebrow whitespace-nowrap">
        {label}
      </span>
      <span
        className={cn(
          'text-xl font-bold tabular-nums leading-tight tracking-tight',
          accent ? 'text-primary' : 'text-foreground'
        )}
      >
        {value}
      </span>
      <span className="text-2xs text-muted-foreground">{unit}</span>
      {sub && (
        <span className="text-2xs text-muted-foreground mt-0.5">{sub}</span>
      )}
    </div>
  );
}

export function KpiStrip({ stats, className }: KpiStripProps) {
  const { t } = useTranslation();

  if (stats.totalTools === 0) return null;

  // stats.totalMinutes / avgMinutesPerTool hold SECONDS (see dashboard.ts); /60 gives minutes.
  const totalMinutes = Math.round(stats.totalMinutes / 60);
  const avgMinutesPerTool = Math.round(stats.avgMinutesPerTool / 60);
  const avgVolPerTool = Math.round(stats.totalVolume / stats.totalTools);

  const cells: CellProps[] = [
    {
      label: t('analytics.dashboard.kpi.avgLife'),
      value: fmt.format(Math.round(stats.avgPieces)),
      unit: t('analytics.dashboard.kpi.unit.piecesPerTool'),
      sub: t('analytics.dashboard.kpi.avgLifeSub', {
        best: fmt.format(stats.bestLife),
        worst: fmt.format(stats.worstLife),
      }),
      accent: true,
    },
    {
      label: t('analytics.dashboard.kpi.totalPieces'),
      value: fmt.format(stats.totalPieces),
      unit: t('analytics.dashboard.kpi.unit.pieces'),
      sub: t('analytics.dashboard.kpi.totalPiecesSub', { n: stats.totalTools }),
    },
    {
      label: t('analytics.dashboard.kpi.totalTime'),
      value: fmt.format(totalMinutes),
      unit: t('analytics.dashboard.kpi.unit.minutes'),
      sub: t('analytics.dashboard.kpi.totalTimeSub', { n: avgMinutesPerTool }),
    },
    ...(stats.avgMinutesPerPiece !== null
      ? [
          {
            label: t('analytics.dashboard.kpi.avgTimePerPiece'),
            value: fmtDec(stats.avgMinutesPerPiece),
            // avgMinutesPerPiece is seconds per piece (stored seconds / pieces)
            unit: t('analytics.dashboard.kpi.unit.secPerPiece'),
          } satisfies CellProps,
        ]
      : []),
    ...(stats.totalVolume > 0
      ? [
          {
            label: t('analytics.dashboard.kpi.totalVolume'),
            value: fmt.format(Math.round(stats.totalVolume)),
            unit: t('analytics.dashboard.kpi.unit.cm3'),
            sub: t('analytics.dashboard.kpi.totalVolumeSub', {
              n: fmt.format(avgVolPerTool),
            }),
          } satisfies CellProps,
        ]
      : []),
    ...(stats.avgMalround !== null
      ? [
          {
            label: t('analytics.dashboard.kpi.avgMalround'),
            value: fmtDec(stats.avgMalround),
            unit: t('analytics.dashboard.kpi.unit.um'),
          } satisfies CellProps,
        ]
      : []),
  ];

  return (
    <Card padding="none" className={cn('border-l-2 border-l-primary overflow-hidden', className)}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>
        {cells.map((cell) => (
          <Cell key={cell.label} {...cell} />
        ))}
      </div>
    </Card>
  );
}
